import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Identity } from '@wajeez-web/core/api-base';
import {
  Button,
  TextInput,
  NumberInput,
  InputSelect,
  TextAreaInput,
  SwitchInput,
} from '@wajeez-web/components/ui';
import generateRandomPassword from 'libs/core/utils/src/lib/helpers/generateRandomPassword';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { RefreshIcon } from '@heroicons/react/outline';
import { useCommonDispatch } from '@wajeez-web/stores/common';
import { CustomerCareActions } from '@wajeez-web/stores/customers';
export const GenderOptions = [
  { label: 'Male', value: Identity.Gender.Male },
  { label: 'Female', value: Identity.Gender.Female },
];

const UserDetailsElementForm = ({
  data,
  points,
  getData,
}: {
  data: Identity.UserDetailsOutput;
  points: number;
  getData: () => void;
}) => {
  const dispatch = useCommonDispatch();
  const [changeSelectUserPassword, setChangeSelectedUserPassword] = useState<{
    show: boolean;
    value: string;
  }>({ show: false, value: '' });
  const [pointsBody, setPointsbody] = useState<{
    amount: number;
    reason: string;
  }>({ amount: 0, reason: '' });
  const [elementLoading, setElementLoading] = useState<{
    name: boolean;
    email: boolean;
    gender: boolean;
    points: boolean;
    password: boolean;
  }>({
    name: false,
    email: false,
    gender: false,
    points: false,
    password: false,
  });
  const [selectedUserData, setSelectedUserData] = useState<{
    id: string;
    name: string;
    email: string;
    isVerified: boolean;
    isBlocked: boolean;
    gender: Identity.Gender;
    [key: string]: any;
  }>({
    id: '',
    name: '',
    email: '',
    isVerified: true,
    gender: 0,
    isBlocked: false,
  });

  const { t } = useTranslation();

  useEffect(() => {
    data.id &&
      setSelectedUserData({
        id: data.id,
        name: data.name || '',
        email: data.email || '',
        gender: data.gender || 0,
        isVerified: data.isVerified || false,
        isBlocked: data.isBlocked || false,
      });
  }, [data.id]);

  useEffect(() => {
    setPointsbody({ ...pointsBody, amount: points });
  }, [points]);

  const OnUserChange = (name: string, val: any) => {
    setSelectedUserData({ ...selectedUserData, [name]: val });
  };

  const OnPointsChange = (name: string, val: any) => {
    setPointsbody({ ...pointsBody, [name]: val });
  };
  return (
    <div className=" space-y-5">
      <div className="flex justify-between border-b py-4">
        <div className="text-lg font-bold">
          <ReturnTranslatedText
            origintext="User Details"
            text={`general.${'User Details'}`}
          />
        </div>
      </div>
      <div className=" space-y-4 divide-y">
        <div className={`grid grid-cols-${2} gap-3 space-y-2 `}>
          <div className={`md:col-span-${2} col-span-${2}`}>
            <TextInput
              value={selectedUserData.name}
              label={'Name'}
              name={'name'}
              onChange={OnUserChange}
            />
          </div>

          <div className="flex justify-end col-span-2">
            <Button
              title={'change'}
              bg={'primary'}
              loading={elementLoading.name}
              color={'white'}
              onClick={async () => {
                setElementLoading({ ...elementLoading, name: true });
                const res = await dispatch(
                  CustomerCareActions.updateUserNameAsync({
                    userId: selectedUserData.id,
                    name: selectedUserData.name,
                  })
                );
                if (
                  res.meta.requestStatus === 'fulfilled' ||
                  res.meta.requestStatus === 'rejected'
                ) {
                  setElementLoading({ ...elementLoading, name: false });
                  getData();
                }
              }}
            />
          </div>
        </div>
        <div className={`grid grid-cols-${2} gap-3 space-y-2 `}>
          <div className={`md:col-span-${2} col-span-${2}`}>
            <TextInput
              value={selectedUserData.email}
              label={'Email'}
              name={'email'}
              onChange={OnUserChange}
            />
          </div>

          <div className={`md:col-span-${2} col-span-${2}`}>
            <SwitchInput
              value={selectedUserData.isVerified}
              label={'Is Verified'}
              name={'isVerified'}
              onChange={OnUserChange}
            />
          </div>
          <div className="flex justify-end col-span-2">
            <Button
              title={'change'}
              loading={elementLoading.email}
              bg={'primary'}
              color={'white'}
              onClick={async () => {
                setElementLoading({ ...elementLoading, email: true });
                const res = await dispatch(
                  CustomerCareActions.setUserEmailAsync({
                    userId: selectedUserData.id,
                    email: selectedUserData.email,
                    isVerified: selectedUserData.isVerified,
                  })
                );
                if (
                  res.meta.requestStatus === 'fulfilled' ||
                  res.meta.requestStatus === 'rejected'
                ) {
                  setElementLoading({ ...elementLoading, email: false });
                }
              }}
            />
          </div>
        </div>

        <div className={`grid grid-cols-${2} gap-3 space-y-2 `}>
          <div className={`md:col-span-${2} col-span-${2}`}>
            <InputSelect
              value={GenderOptions.find(
                (item) => item.value === selectedUserData.gender
              )}
              options={GenderOptions.map((option) => {
                const { label } = option;

                option.label = t(`selects.labels.${label}`, label || '');

                return option;
              })}
              label={'Gender'}
              name={'gender'}
              onChange={(name, value: any) =>
                setSelectedUserData({
                  ...selectedUserData,
                  [name]: value.value,
                })
              }
            />
          </div>

          <div className="flex justify-end col-span-2">
            <Button
              title={'change'}
              bg={'primary'}
              loading={elementLoading.gender}
              color={'white'}
              onClick={async () => {
                setElementLoading({ ...elementLoading, gender: true });
                const res = await dispatch(
                  CustomerCareActions.updateUserGenderAsync({
                    userId: selectedUserData.id,
                    gender: selectedUserData.gender,
                  })
                );
                if (
                  res.meta.requestStatus === 'fulfilled' ||
                  res.meta.requestStatus === 'rejected'
                ) {
                  setElementLoading({
                    ...elementLoading,
                    gender: false,
                  });
                  getData();
                }
              }}
            />
          </div>
        </div>

        <div className={`grid grid-cols-${2} gap-3 space-y-2 `}>
          <div className={`md:col-span-${2} col-span-${2}`}>
            <NumberInput
              value={pointsBody.amount}
              label={'Points Amount'}
              name={'amount'}
              onChange={OnPointsChange}
            />

            <TextAreaInput
              value={pointsBody.reason}
              label={'Reason'}
              name={'reason'}
              onChange={OnPointsChange}
            />
          </div>

          <div className="flex justify-end col-span-2">
            <Button
              title={'change'}
              loading={elementLoading.points}
              bg={'primary'}
              color={'white'}
              onClick={async () => {
                setElementLoading({ ...elementLoading, points: true });
                const res = await dispatch(
                  CustomerCareActions.addPointsToUserAsync({
                    userId: selectedUserData.id,
                    amount: pointsBody.amount,
                    reason: pointsBody.reason,
                  })
                );
                if (
                  res.meta.requestStatus === 'fulfilled' ||
                  res.meta.requestStatus === 'rejected'
                ) {
                  setElementLoading({
                    ...elementLoading,
                    points: false,
                  });
                  getData();
                }
              }}
            />
          </div>
        </div>
        <div>
          {!changeSelectUserPassword && (
            <p className=" text-textSecondary space-y-5 pt-2">
              <ReturnTranslatedText
                origintext="Do you want to change user password?"
                text={`general.${'Do you want to change user password?'}`}
              />
              <span
                onClick={() => {
                  setChangeSelectedUserPassword({ value: '', show: true });
                }}
                className=" text-primary cursor-pointer"
              >
                {' '}
                <ReturnTranslatedText
                  origintext="Yes"
                  text={`general.${'Yes'}`}
                />
              </span>
            </p>
          )}

          {changeSelectUserPassword && (
            <div className=" space-y-5 pt-2 ">
              <div className={`grid grid-cols-${2} gap-3 `}>
                <div className={`md:col-span-${2} col-span-${2}`}>
                  <TextInput
                    value={changeSelectUserPassword.value}
                    label={'Password'}
                    name={'password'}
                    suffix={
                      <Button
                        type="text"
                        bg={'white'}
                        color="textPrimary hover:text-gray-500"
                        Icon={RefreshIcon}
                        onClick={() =>
                          OnUserChange('password', generateRandomPassword())
                        }
                      />
                    }
                    onChange={(name, value) => {
                      setChangeSelectedUserPassword({
                        ...changeSelectUserPassword,
                        value,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  title={'change'}
                  bg={'primary'}
                  loading={elementLoading.password}
                  color={'white'}
                  onClick={async () => {
                    setElementLoading({ ...elementLoading, password: true });
                    const res = await dispatch(
                      CustomerCareActions.setUserPasswordAsync({
                        userId: selectedUserData.id,
                        password: changeSelectUserPassword.value,
                      })
                    );
                    if (
                      res.meta.requestStatus === 'fulfilled' ||
                      res.meta.requestStatus === 'rejected'
                    ) {
                      setElementLoading({
                        ...elementLoading,
                        password: false,
                      });
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsElementForm;
